/**
 * Leave Reports (LEAVE-REGISTRY-P4/P5)
 * HR/Admin: balance summary + request reports with CSV export.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileSpreadsheet, Download, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import LeaveBalancesGrouped from './LeaveBalancesGrouped';

const API = process.env.REACT_APP_BACKEND_URL || '';

const REPORTS = [
  { id: 'balances', label: 'Leave Balances' },
  { id: 'pending', label: 'Pending Approvals', status: 'pending,pending_hr' },
  { id: 'approved', label: 'Approved Leave', status: 'approved' },
  { id: 'rejected', label: 'Rejected Leave', status: 'rejected' },
  // LEAVE-REPORTS-UI-003 (Sep 2026): "Full History" (all statuses) replaced by a
  // dedicated Cancelled tab — every status now has exactly one non-overlapping tab.
  { id: 'cancelled', label: 'Cancelled Leave', status: 'cancelled' },
  { id: 'unpaid', label: 'Unpaid Leave', unpaid: true },
];

const PAGE_SIZES = [25, 50, 100];
const fmtUs = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '');

const LeaveReports = () => {
  const [report, setReport] = useState('balances');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  // LEAVE-REPORTS-EXPORT-001: export needs an explicit business-date range
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const def = REPORTS.find(r => r.id === report);
  const isBalances = report === 'balances';

  const buildParams = (format) => {
    const params = { format };
    if (def.status) params.status = def.status;
    if (def.unpaid) params.unpaid_only = true;
    return params;
  };

  const endpoint = isBalances
    ? `${API}/api/leave-registry/reports/balance-summary`
    : `${API}/api/leave-registry/reports/requests`;

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...buildParams('json'), page, page_size: pageSize };
      if (isBalances) params.group_by = 'employee'; // LEAVE-BAL-GROUP-001: on-screen view is per employee
      const res = await axios.get(endpoint, { params });
      setRows(res.data.rows || []);
      setMeta({ total: res.data.total ?? (res.data.rows || []).length, page: res.data.page || page, pages: res.data.pages || 1 });
      setExpanded(new Set());
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [report, page, pageSize]); // report drives endpoint + params

  useEffect(() => { fetchRows(); }, [fetchRows]);
  const changeReport = (id) => { setReport(id); setPage(1); };
  const changePageSize = (n) => { setPageSize(n); setPage(1); };

  // Business date per report: request tabs = leave period (start_date–end_date overlap, the
  // backend's existing start/end semantics); balances are annual → the year of From Date.
  const dateError = fromDate && toDate && fromDate > toDate ? 'From Date cannot be later than To Date' : '';
  const balancesYearMismatch = isBalances && fromDate && toDate && fromDate.slice(0, 4) !== toDate.slice(0, 4);
  const canExport = !!fromDate && !!toDate && !dateError && !balancesYearMismatch && !exporting;
  const rangeHint = isBalances
    ? 'Balances are annual — export covers the balance year of the selected dates (both dates must be in the same year).'
    : 'Filters by leave period: every request whose leave dates fall within or overlap the range.';

  const exportCsv = async () => {
    if (!canExport) return;
    setExporting(true);
    try {
      const params = buildParams('csv');
      if (isBalances) { params.year = Number(fromDate.slice(0, 4)); params.range_from = fromDate; params.range_to = toDate; }
      else { params.start = fromDate; params.end = toDate; }
      const res = await axios.get(endpoint, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave_${report}_report_${fromDate}_to_${toDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // LEAVE-REPORTS-UI-001 (Jul 2026): email + low-frequency fields move out of
  // the main grid into an expandable per-row detail panel so every essential
  // column fits without horizontal scrolling. CSV export is backend-generated
  // and still contains every field, including email.
  // LEAVE-REPORTS-UI-005: email is not shown in the report UI (still in the profile + CSV export).
  const DETAIL_COLUMNS = ['reason', 'approved_by', 'hr_approved_by', 'applied_at', 'submitted_on_behalf_by'];
  const HIDDEN_COLUMNS = ['email'];
  // LEAVE-REPORTS-UI-004: shown only when the request was submitted on behalf
  // (value is the historically stored submitter; self-submitted rows hide it).
  const OPTIONAL_DETAIL_COLUMNS = ['submitted_on_behalf_by'];
  const LABEL_OVERRIDES = { submitted_on_behalf_by: 'Submitted On Behalf By', hr_approved_by: 'HR Approved By', start_date: 'Leave From', end_date: 'Leave To' };
  // LEAVE-REPORTS-UI-002 (Jul 2026): content-based column widths — short
  // values (role, numeric balances, dates) get compact fixed widths and the
  // flexible columns (employee, leave_type) absorb the remaining space, so
  // no column hogs width and nothing needs horizontal scrolling.
  const COL_WIDTHS = {
    role: 'w-20',
    entitled: 'w-14',
    used: 'w-12',
    pending: 'w-14',
    available: 'w-16',
    carry_forward: 'w-16',
    manual_adjustment: 'w-20',
    start_date: 'w-24',
    end_date: 'w-24',
    day_part: 'w-14',
    days: 'w-10',
    status: 'w-20',
  };
  const allColumns = rows.length ? Object.keys(rows[0]) : [];
  const columns = allColumns.filter(c => !DETAIL_COLUMNS.includes(c) && !HIDDEN_COLUMNS.includes(c));
  const detailColumns = allColumns.filter(c => DETAIL_COLUMNS.includes(c));
  const label = (c) => LABEL_OVERRIDES[c] || c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

  const toggleRow = (i) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="leave-reports">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Leave Reports</h3>
        </div>
        <div className="flex items-end gap-2 flex-wrap" data-testid="report-export-controls">
          <label className="text-xs text-gray-600">
            <span className="block mb-0.5 font-medium">From Date</span>
            <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)}
              className="h-9 px-2 text-sm border border-gray-300 rounded-lg bg-white" data-testid="report-export-from" />
          </label>
          <label className="text-xs text-gray-600">
            <span className="block mb-0.5 font-medium">To Date</span>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)}
              className="h-9 px-2 text-sm border border-gray-300 rounded-lg bg-white" data-testid="report-export-to" />
          </label>
          <button onClick={exportCsv} disabled={!canExport} data-testid="report-export-csv"
            title={canExport ? `Export ${def.label} for the selected range` : 'Select From Date and To Date to export'}
            className="h-9 flex items-center gap-1.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
          </button>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 -mt-2">
        {dateError || balancesYearMismatch
          ? <span className="text-xs text-red-600" data-testid="report-export-error">{dateError || 'Balance exports must stay within one year'}</span>
          : <span className="text-[11px] text-gray-400" data-testid="report-export-hint">{rangeHint}</span>}
      </div>

      <div className="flex gap-2 flex-wrap">
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => changeReport(r.id)} data-testid={`report-tab-${r.id}`}
            className={`px-3 py-1.5 rounded-lg border text-sm ${report === r.id ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* LEAVE-REPORTS-CONTEXT-001: make the reporting period explicit above the results */}
      <div className="flex items-center gap-2 flex-wrap rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-slate-700" data-testid="report-context">
        {isBalances ? (
          <>
            <span className="font-semibold" data-testid="report-context-year">Balance Year: {fromDate ? fromDate.slice(0, 4) : new Date().getFullYear()}</span>
            <span className="text-slate-400">·</span>
            <span>Balances are annual totals — one row per employee; expand a row for the leave-type breakdown.</span>
          </>
        ) : (
          <>
            <span className="font-semibold">{def.label}</span>
            <span className="text-slate-400">·</span>
            <span>Each row shows its leave period (Leave From / Leave To).</span>
          </>
        )}
        {fromDate && toDate && !dateError && (
          <>
            <span className="text-slate-400">·</span>
            <span data-testid="report-context-range">Selected export range: <b>{fmtUs(fromDate)} – {fmtUs(toDate)}</b>{isBalances ? '' : ' (leave period overlap)'}</span>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-10">No data for this report.</p>
      ) : isBalances ? (
        <LeaveBalancesGrouped rows={rows} expanded={expanded} onToggle={toggleRow} />
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="tf-thead">
              <tr>
                {detailColumns.length > 0 && <th className="w-7 px-1 py-2" aria-label="Expand" />}
                {columns.map(c => (
                  <th key={c} className={`px-2 py-2 text-left font-medium text-gray-600 leading-tight ${COL_WIDTHS[c] || ''}`}>
                    {label(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, i) => (
                <React.Fragment key={i}>
                  <tr
                    className={`hover:bg-slate-50 ${detailColumns.length ? 'cursor-pointer' : ''}`}
                    onClick={detailColumns.length ? () => toggleRow(i) : undefined}
                    data-testid={`report-row-${i}`}
                  >
                    {detailColumns.length > 0 && (
                      <td className="px-1 py-1.5 text-gray-400">
                        <ChevronRight
                          className={`w-3.5 h-3.5 transition-transform ${expanded.has(i) ? 'rotate-90' : ''}`}
                          data-testid={`report-row-toggle-${i}`}
                        />
                      </td>
                    )}
                    {columns.map(c => (
                      <td key={c} className="px-2 py-1.5 text-gray-700">
                        {c === 'role'
                          ? String(r[c] ?? '—').replace(/_/g, ' ')
                          : String(r[c] ?? '—')}
                      </td>
                    ))}
                  </tr>
                  {expanded.has(i) && detailColumns.length > 0 && (
                    <tr className="bg-slate-50/60" data-testid={`report-row-detail-${i}`}>
                      <td />
                      <td colSpan={columns.length} className="px-3 py-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5">
                          {detailColumns
                            .filter(c => !OPTIONAL_DETAIL_COLUMNS.includes(c) || (r[c] != null && r[c] !== ''))
                            .map(c => (
                            <div key={c} className="flex gap-2 min-w-0" data-testid={`report-detail-${c}-${i}`}>
                              <span className="text-gray-400 shrink-0">{label(c)}:</span>
                              <span className="text-gray-700 break-words min-w-0">{String(r[c] ?? '—')}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && meta.total > 0 && (() => {
        const first = (meta.page - 1) * pageSize + 1;
        const last = Math.min(meta.page * pageSize, meta.total);
        const pageNums = Array.from({ length: meta.pages }, (_, i) => i + 1).filter(n => n === 1 || n === meta.pages || Math.abs(n - meta.page) <= 2);
        const btn = 'h-8 min-w-8 px-2 rounded-md border text-xs disabled:opacity-40 disabled:cursor-not-allowed';
        return (
          <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="report-pagination">
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Rows per page</span>
              <select value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))} className="h-8 px-2 border border-gray-300 rounded-md bg-white text-xs" data-testid="report-page-size">
                {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-gray-500" data-testid="report-pagination-summary">Showing {first}–{last} of {meta.total} {isBalances ? 'employees' : 'records'}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={meta.page <= 1} className={`${btn} border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center gap-1`} data-testid="report-page-prev">
                <ChevronLeft className="w-3.5 h-3.5" /> Previous
              </button>
              {pageNums.map((n, i) => (
                <React.Fragment key={n}>
                  {i > 0 && pageNums[i - 1] !== n - 1 && <span className="px-1 text-xs text-gray-400">…</span>}
                  <button onClick={() => setPage(n)} aria-current={n === meta.page ? 'page' : undefined} data-testid={`report-page-${n}`}
                    className={`${btn} ${n === meta.page ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{n}</button>
                </React.Fragment>
              ))}
              <button onClick={() => setPage(p => Math.min(meta.pages, p + 1))} disabled={meta.page >= meta.pages} className={`${btn} border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center gap-1`} data-testid="report-page-next">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LeaveReports;
